import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from './languages';
import { useLanguageSync } from './useLanguageSync';
import './LanguageSelector.css';

interface LanguageSelectorProps {
  backendUrl?: string;
  getToken?: () => string | null;
  className?: string;
}

export function LanguageSelector({ backendUrl, getToken, className }: LanguageSelectorProps) {
  const { i18n, t } = useTranslation();
  const { changeLanguage } = useLanguageSync({ backendUrl, getToken });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return SUPPORTED_LANGUAGES;
    return SUPPORTED_LANGUAGES.filter(
      (l) =>
        l.nativeName.toLowerCase().includes(q) ||
        l.englishName.toLowerCase().includes(q) ||
        l.code.toLowerCase().includes(q),
    );
  }, [search]);

  const tier1 = filtered.filter((l) => l.tier === 1);
  const tier2 = filtered.filter((l) => l.tier === 2);
  const activeLng = i18n.language?.split('-')[0] ?? 'en';
  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === activeLng || l.code === i18n.language) ??
    SUPPORTED_LANGUAGES[0];

  const select = async (code: string) => {
    await changeLanguage(code);
    setOpen(false);
    setSearch('');
  };

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  const rootClass = ['lang-selector', className].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      <button
        type="button"
        className="lang-selector__trigger"
        onClick={() => setOpen((o) => !o)}
        title={t('lang.change')}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg
          className="lang-selector__globe"
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <span className="lang-selector__current">
          <span className="lang-selector__current-native">{current.nativeName}</span>
        </span>
        <span className="lang-selector__chevron" aria-hidden="true" />
      </button>

      {open && (
        <>
          <div className="lang-selector__backdrop" onClick={close} aria-hidden="true" />
          <div
            className="lang-selector__panel"
            role="dialog"
            aria-modal="true"
            aria-label={t('lang.select')}
          >
            <div className="lang-selector__header">
              <div className="lang-selector__header-row">
                <span className="lang-selector__title">{t('lang.select')}</span>
                <button type="button" className="lang-selector__close" onClick={close} aria-label={t('common.close', 'Close')}>
                  ✕
                </button>
              </div>
              <input
                ref={inputRef}
                type="search"
                className="lang-selector__search"
                placeholder={t('lang.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="lang-selector__list">
              {tier1.length > 0 && (
                <>
                  <div className="lang-selector__tier">
                    {t('lang.tier1')} ({tier1.length})
                  </div>
                  {tier1.map((lang) => {
                    const isActive = lang.code === activeLng || lang.code === i18n.language;
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        className={`lang-selector__item${isActive ? ' lang-selector__item--active' : ''}`}
                        onClick={() => select(lang.code)}
                      >
                        <span className="lang-selector__names">
                          <span className="lang-selector__native">{lang.nativeName}</span>
                          {lang.nativeName !== lang.englishName && (
                            <span className="lang-selector__english">{lang.englishName}</span>
                          )}
                        </span>
                        <span className="lang-selector__meta">
                          {lang.rtl && <span className="lang-selector__rtl">RTL</span>}
                          {isActive && <span className="lang-selector__check" aria-hidden="true">✓</span>}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}

              {tier2.length > 0 && (
                <>
                  <div className="lang-selector__tier">{t('lang.tier2')} ({tier2.length})</div>
                  {tier2.map((lang) => {
                    const isActive = lang.code === activeLng || lang.code === i18n.language;
                    return (
                      <button
                        key={lang.code}
                        type="button"
                        className={`lang-selector__item lang-selector__item--secondary${isActive ? ' lang-selector__item--active' : ''}`}
                        onClick={() => select(lang.code)}
                      >
                        <span className="lang-selector__names">
                          <span className="lang-selector__native">{lang.nativeName}</span>
                          {lang.nativeName !== lang.englishName && (
                            <span className="lang-selector__english">{lang.englishName}</span>
                          )}
                        </span>
                        <span className="lang-selector__meta">
                          {lang.rtl && <span className="lang-selector__rtl">RTL</span>}
                          {isActive && <span className="lang-selector__check" aria-hidden="true">✓</span>}
                        </span>
                      </button>
                    );
                  })}
                </>
              )}

              {filtered.length === 0 && (
                <div className="lang-selector__empty">{t('lang.noResults')}</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
